import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { Permission, hasPermission as hasPermissionHelper } from 'extra/types/Permissions'
import { api, HTTPError } from '../api/client'
import type { IAPIPrincipal } from 'extra/types/ApiModels'

interface AuthContextType {
  token: string | null
  isAuthenticated: boolean
  authLoading: boolean
  user: IAPIPrincipal | null
  login: (token: string, user: IAPIPrincipal) => void
  logout: () => void
  hasPermission: (permission: Permission) => boolean
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_KEY = 'pantheon_jwt_token'

export function AuthProvider ({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY))
  const [user, setUser] = useState<IAPIPrincipal | null>(null)
  const [authLoading, setAuthLoading] = useState(() => localStorage.getItem(TOKEN_KEY) !== null)

  const isAuthenticated = token !== null

  const login = (newToken: string, newUser: IAPIPrincipal) => {
    localStorage.setItem(TOKEN_KEY, newToken)
    setToken(newToken)
    setUser(newUser)
  }

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY)
    setToken(null)
    setUser(null)
  }

  const hasPermission = useCallback((permission: Permission): boolean => hasPermissionHelper(user?.permissions ?? null, permission), [user])

  const refreshUser = useCallback(async () => {
    try {
      const me = await api.auth.me()
      setUser(me)
    } catch (error) {
      if (error instanceof HTTPError && error.response.status >= 400 && error.response.status < 500) {
        logout()
      }
    }
  }, [])

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY)
      if (storedToken) {
        setToken(storedToken)
        try {
          const me = await api.auth.me()
          setUser(me)
        } catch (error) {
          if (error instanceof HTTPError && error.response.status >= 400 && error.response.status < 500) {
            logout()
          }
        } finally {
          setAuthLoading(false)
        }
      }
    }
    initAuth()
  }, [])

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, authLoading, user, login, logout, hasPermission, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth () {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
